import { Box } from "@mui/material";
import type { FC } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { NotificationsKeys } from "web_myplann/Api/Notifications/notificatioKeys";
import { useGetNotificationsGroupedCountQuery } from "web_myplann/Api/Notifications/useNotificationGroupedCountQuery";
import { useNotificationsGroupedQuery } from "web_myplann/Api/Notifications/useNotificationsGroupedQuery";
import {
	NOTIFICATION_TAB_STORAGE_KEY,
	NotificationTab,
} from "web_myplann/Dashboard/notificationsTabs";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannTabs } from "web_myplann/Shared/Components/MyPlannTabs";
import { NativePullToRefresh } from "web_myplann/Shared/Components/NativePullToRefresh";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { useResolveTabFromStorage } from "web_myplann/Shared/Hooks/useResolveTabFromStorage";

export type NotificationsTabContext = {
	unreadCount: number;
};

const tabFromPath = (pathname: string): NotificationTab =>
	pathname === Routing.NotificationsAll.path()
		? NotificationTab.All
		: NotificationTab.Unread;

export const NotificationsTabsLayout: FC = () => {
	const { t } = useResource();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const isResolving = useResolveTabFromStorage({
		parentPath: Routing.Notifications.path(),
		storageKey: NOTIFICATION_TAB_STORAGE_KEY,
		tabs: [
			{
				value: NotificationTab.Unread,
				path: Routing.NotificationsUnread.path(),
			},
			{ value: NotificationTab.All, path: Routing.NotificationsAll.path() },
		],
	});

	const activeTab = tabFromPath(pathname);

	const { data: notificationsGroupedCount } =
		useGetNotificationsGroupedCountQuery(true);
	const unreadCount = notificationsGroupedCount?.count ?? 0;

	// Keep both tab queries observed at the layout level so pull-to-refresh
	// (refetchType: "active") refetches the inactive tab too. Data itself is
	// consumed by the active child via the shared react-query cache.
	useNotificationsGroupedQuery(true);
	useNotificationsGroupedQuery(false);

	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		if (newValue === activeTab) return;
		localStorage.setItem(NOTIFICATION_TAB_STORAGE_KEY, String(newValue));
		navigate(
			newValue === NotificationTab.All
				? Routing.NotificationsAll.path()
				: Routing.NotificationsUnread.path(),
			{ replace: true },
		);
	};

	const outletContext: NotificationsTabContext = { unreadCount };

	if (isResolving) {
		return (
			<MyPlannLayout
				disableContentPadding
				mainSx={{
					overflow: "visible",
					display: "flex",
					flexDirection: "column",
				}}
				topBar={
					<NavigationHeader
						title={t(Resources.Notifications.title)}
						defaultRoute={Routing.Dashboard.path()}
						disableBottomPadding
					/>
				}
			/>
		);
	}

	return (
		<MyPlannLayout
			disableContentPadding
			mainSx={{ overflow: "visible", display: "flex", flexDirection: "column" }}
			topBar={
				<NavigationHeader
					title={t(Resources.Notifications.title)}
					defaultRoute={Routing.Dashboard.path()}
					bottomComponent={
						<MyPlannTabs
							value={activeTab}
							onChange={handleTabChange}
							tabs={[
								{
									id: "unread",
									label: `${t(Resources.Notifications.Tabs.unread)} (${unreadCount})`,
								},
								{
									id: "all",
									label: t(Resources.Notifications.Tabs.all),
								},
							]}
						/>
					}
					disableBottomPadding
				/>
			}
		>
			<Box key={pathname} sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
				<NativePullToRefresh
					queryKeys={[
						NotificationsKeys.count(),
						NotificationsKeys.infiniteGroupedList(true),
						NotificationsKeys.infiniteGroupedList(false),
					]}
					spinnerOffset={-25}
				>
					<Box px={1.6}>
						<Outlet context={outletContext} />
					</Box>
				</NativePullToRefresh>
			</Box>
		</MyPlannLayout>
	);
};
